import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  InformationBanner,
  MultipleFileUploader,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { ServiceProviderTemplate } from "../../../../../template/service-provider/default-template";
import { ServiceProviderR } from "../../../routes";
import { useServiceProviderSmartApplyProceduresBulkImportByCSVHooks } from "./hooks";

export const ServiceProviderSmartApplyProceduresBulkImportByCSV: React.FC =
  () => {
    const { id: tenantId } = useParams<{ id: string }>();

    const {
      isBulkImporting,
      isCheckingImportJsonFiles,
      showResultMode,
      importableLimit,
      jsonFiles,
      checkResult,
      displayCheckResult,
      hasCheckErr,
      importable,
      bulkImportResult,
      handleSetJsonFiles,
      handleDelete,
      handleBulkImport,
    } = useServiceProviderSmartApplyProceduresBulkImportByCSVHooks({
      tenantId,
    });

    return (
      <ServiceProviderTemplate
        breadcrumbs={[
          {
            to: ServiceProviderR.Home.link({}),
            title: "Home",
          },
          {
            to: ServiceProviderR.Procedures.link({ id: tenantId }),
            title: "手続き一覧",
          },
          {
            to: ServiceProviderR.SmartApplyProceduresBulkImportByCSV.link({
              id: tenantId,
            }),
            title: "手続き一括インポート",
          },
        ]}
      >
        <Spinner loading={isBulkImporting || isCheckingImportJsonFiles} />
        <div className="columns is-vcentered">
          <div className="column has-text-left">
            <h1 className="title">手続き一括インポート</h1>
          </div>
        </div>
        <div className="mb-6">
          <p>
            csvファイルからエクスポートした手続き設定内容を一括インポートします。一度に登録可能な件数は最大
            {importableLimit}件です。
          </p>
        </div>
        {showResultMode === "showCheckResult" && (
          <>
            {displayCheckResult && (
              <div className="mb-6">
                {hasCheckErr && (
                  <>
                    <div className="mb-4">
                      <WarningBanner message="以下の理由により一括インポートできません。設定内容を確認してください。" />
                    </div>
                    <div className="content">
                      {checkResult.invalidJsons.length > 0 && (
                        <div>
                          <p className="has-text-weight-bold">
                            jsonファイルの形式が正しくありません。
                          </p>
                          <ul
                            className="mb-4"
                            style={{ listStyleType: "disc" }}
                          >
                            {checkResult.invalidJsons.map((filename, i) => (
                              <li key={`bulk-import-by-csv-invalid-json-${i}`}>
                                {filename}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {checkResult.invalidFormSchemas.length > 0 && (
                        <div>
                          <p className="has-text-weight-bold">
                            手続き設定内容が正しくありません。
                          </p>
                          <ul
                            className="mb-4"
                            style={{ listStyleType: "disc" }}
                          >
                            {checkResult.invalidFormSchemas.map(
                              (filename, i) => (
                                <li
                                  key={`bulk-import-by-csv-invalid-form-schema-${i}`}
                                >
                                  {filename}
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                      {checkResult.noPermissionDestManageGroups.length > 0 && (
                        <div>
                          <p className="has-text-weight-bold">
                            操作中のテナントに存在しない移行先管理グループが指定されています。
                          </p>
                          <ul
                            className="mb-4"
                            style={{ listStyleType: "disc" }}
                          >
                            {checkResult.noPermissionDestManageGroups.map(
                              (filename, i) => (
                                <li
                                  key={`bulk-import-by-csv-no-permission-dest-manage-group-${i}`}
                                >
                                  {filename}
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {checkResult.duplicatedCustomPaths.length > 0 && (
                  <>
                    <div className="mb-4">
                      <InformationBanner message="既に登録されている申請開始ページURLが設定されています。インポートする際に設定されている申請開始ページURLは削除され、設定されていない手続きとしてインポートされます。" />
                    </div>
                    <div className="content">
                      <ul style={{ listStyleType: "disc" }}>
                        {checkResult.duplicatedCustomPaths.map(
                          (filename, i) => (
                            <li
                              key={`bulk-import-by-csv-duplicated-custom-path-${i}`}
                            >
                              {filename}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}
            <>
              <div className="mb-4">
                <MultipleFileUploader
                  label="手続き設定ファイル"
                  description="エクスポートしたjson形式の手続き設定ファイルを複数アップロードできます。"
                  required
                  // application/jsonが指定できないため暫定でundefinedを指定しておく
                  // 代わりにファイルアップロード時にtypeを判定する処理を入れている
                  acceptFileType={undefined}
                  value={jsonFiles}
                  setFile={handleSetJsonFiles}
                  // stateの管理が煩雑になるため、アップロード後の単一のファイル削除は許容しない
                  // 本来であれば削除ボタンごと隠したいが、暫定でトーストでエラーを表示する
                  deleteFile={() => {
                    toast.error(
                      "ファイルの削除はできません。設定内容を破棄するか、リロードして再度アップロードしてください。",
                    );
                  }}
                />
              </div>
              {jsonFiles.length > 0 && (
                <>
                  <div className="mb-4 has-text-weight-bold">
                    {jsonFiles.length}件アップロード済み
                  </div>
                  <SecondaryButton
                    text="アップロードした設定内容を破棄する"
                    onClick={handleDelete}
                  />
                </>
              )}
              <div className="mt-6">
                <PrimaryButton
                  text="一括インポートする"
                  onClick={handleBulkImport}
                  disabled={!importable || isBulkImporting}
                />
              </div>
            </>
          </>
        )}
        {showResultMode === "showBulkImportResult" && (
          <>
            {bulkImportResult.successes.length > 0 && (
              <div className="mb-6 content">
                <div className="mb-4">
                  <InformationBanner
                    message={`以下の手続きのインポートが完了しました。`}
                  />
                </div>
                <ul>
                  {bulkImportResult.successes.map((s, i) => (
                    <li key={`bulk-import-by-csv-success-procedure-${i}`}>
                      <Link
                        to={ServiceProviderR.ProcedureDetail.link({
                          orgId: tenantId,
                          procedureId: s.procedureId,
                        })}
                      >
                        手続きID: {s.procedureId} 手続き名: {s.procedureName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bulkImportResult.failures.length > 0 && (
              <div className="mb-6 content">
                <div className="mb-4">
                  <WarningBanner
                    message={`以下のファイルのインポートに失敗しました。`}
                  />
                </div>
                <ul>
                  {bulkImportResult.failures.map((filename, i) => (
                    <li key={`bulk-import-by-csv-failure-file-${i}`}>
                      {filename}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </ServiceProviderTemplate>
    );
  };
